
import { useState, useMemo } from "react"
import { Card } from "@/components/ui/card"
import { Slider } from "@/components/ui/slider"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"
import { TrendingUp, DollarSign, Target, Calendar } from "lucide-react"
import { NotificationBell } from "@/components/notification-bell"

interface GrowthProps {
  onNavigate?: (view: string) => void
}

export function Growth({ onNavigate }: GrowthProps = {}) {
  const [income, setIncome] = useState([5000])
  const [expenses, setExpenses] = useState([3000])
  const [timelineYears, setTimelineYears] = useState([10])
  const [interestRate, setInterestRate] = useState([3])
  const [interestRateInput, setInterestRateInput] = useState("3.0")

  // Input change handlers with validation
  const handleIncomeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0
    const clampedValue = Math.max(0, Math.min(20000, value))
    setIncome([clampedValue])
  }

  const handleExpensesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0
    const clampedValue = Math.max(0, Math.min(20000, value))
    setExpenses([clampedValue])
  }

  const handleTimelineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 1
    const clampedValue = Math.max(1, Math.min(60, value))
    setTimelineYears([clampedValue])
  }

  const handleInterestRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInterestRateInput(e.target.value)
  }

  const handleInterestRateBlur = () => {
    const value = parseFloat(interestRateInput) || 0
    const clampedValue = Math.max(0, Math.min(10, value))
    setInterestRate([clampedValue])
    setInterestRateInput(clampedValue.toFixed(1))
  }

  const handleInterestRateSliderChange = (value: number[]) => {
    setInterestRate(value)
    setInterestRateInput(value[0].toFixed(1))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.currentTarget.blur()
    }
  }

  // Calculate key metrics - memoized for performance
  const currentIncome = income[0]
  const currentExpenses = expenses[0]
  const monthlySavings = currentIncome - currentExpenses
  const actualSavingsRate = currentIncome > 0 ? (monthlySavings / currentIncome) * 100 : 0
  const timelineMonths = timelineYears[0] * 12

  // Compound interest calculation using adjustable rate - memoized
  const annualRate = interestRate[0] / 100
  const monthlyInterestRate = annualRate / 12
  
  const futureValue = useMemo(() => {
    if (monthlySavings <= 0) return 0
    if (monthlyInterestRate === 0) return monthlySavings * timelineMonths
    return monthlySavings * ((Math.pow(1 + monthlyInterestRate, timelineMonths) - 1) / monthlyInterestRate)
  }, [monthlySavings, monthlyInterestRate, timelineMonths])

  const simpleTotal = useMemo(() => 
    monthlySavings * timelineMonths,
    [monthlySavings, timelineMonths]
  )

  const interestEarned = useMemo(() => 
    futureValue - simpleTotal,
    [futureValue, simpleTotal]
  )

  // Generate chart data - memoized with adaptive data points for instant rendering
  const chartData = useMemo(() => {
    const data = []
    // Adaptive data points based on timeline for optimal performance
    const getOptimalDataPoints = (months: number) => {
      if (months <= 24) return months // Monthly for 0-2 years
      if (months <= 60) return 40 // ~1.5 month intervals for 2-5 years
      if (months <= 240) return 60 // Quarterly for 5-20 years
      return 80 // Semi-annual for 20+ years
    }
    const dataPoints = getOptimalDataPoints(timelineMonths)
    const step = Math.max(1, Math.floor(timelineMonths / dataPoints))

    for (let month = 0; month <= timelineMonths; month += step) {
      const withInterest = monthlySavings > 0
        ? monthlyInterestRate === 0
          ? monthlySavings * month
          : monthlySavings * ((Math.pow(1 + monthlyInterestRate, month) - 1) / monthlyInterestRate)
        : 0
      const withoutInterest = monthlySavings * month

      data.push({
        month: month === 0 ? 'Start' : `${Math.floor(month / 12)}y ${month % 12}m`,
        monthNum: month,
        withInterest: Math.round(withInterest),
        withoutInterest: Math.round(withoutInterest),
      })
    }
    return data
  }, [monthlySavings, monthlyInterestRate, timelineMonths])

  // Memoize XAxis interval for performance
  const xAxisInterval = useMemo(() => 
    Math.floor(chartData.length / 6),
    [chartData.length]
  )

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}k`
    }
    return `$${Math.round(value)}`
  }

  const formatCurrencyFull = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  return (
    <div className="space-y-6 p-4 pb-24">
      <div className="max-w-md mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Financial Growth</h1>
          <p className="text-sm text-muted-foreground mt-1">Plan your financial future</p>
        </div>
        <NotificationBell />
      </div>

      {/* Projection Summary */}
      <Card className="p-6 bg-gradient-to-br from-income to-income/80 text-white">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            <p className="text-sm opacity-90">Projected Savings</p>
          </div>
          <h2 className="text-3xl font-bold">{formatCurrencyFull(futureValue)}</h2>
          <div className="flex items-center justify-between text-sm">
            <span className="opacity-90">After {timelineYears[0]} {timelineYears[0] === 1 ? 'year' : 'years'}</span>
            <span className="font-semibold">+{formatCurrency(interestEarned)} interest</span>
          </div>
        </div>
      </Card>

      {/* Current vs Future Comparison */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4">
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Monthly Savings</p>
            <p className="text-2xl font-bold text-income">{formatCurrency(monthlySavings)}</p>
            <p className="text-xs text-muted-foreground">{actualSavingsRate.toFixed(1)}% of income</p>
          </div>
        </Card>
        <Card className="p-4">
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Total Growth</p>
            <p className="text-2xl font-bold text-primary">
              {futureValue > 0 ? `${((interestEarned / simpleTotal) * 100).toFixed(1)}%` : '0%'}
            </p>
            <p className="text-xs text-muted-foreground">via compound interest</p>
          </div>
        </Card>
      </div>

      {/* Growth Chart */}
      <Card className="p-6">
        <div className="space-y-4">
          <div>
            <h3 className="text-base font-semibold">Savings Growth Projection</h3>
            <p className="text-sm text-muted-foreground">
              {interestRate[0] > 0
                ? `With ${interestRate[0].toFixed(1)}% annual return (compounding monthly)`
                : 'Without interest (simple accumulation)'}
            </p>
          </div>

          {monthlySavings <= 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>Adjust income and expenses to see growth projection</p>
            </div>
          ) : (
            <div className="w-full -mx-2 sm:mx-0">
              <ChartContainer
                config={{
                  withInterest: {
                    label: "With Interest",
                    color: "var(--income)",
                  },
                  withoutInterest: {
                    label: "Without Interest",
                    color: "var(--chart-2)",
                  },
                }}
                className="h-[300px] sm:h-[350px] w-full"
              >
                <AreaChart
                  data={chartData}
                  margin={{
                    top: 10,
                    right: 10,
                    left: 0,
                    bottom: 5,
                  }}
                >
                  <defs>
                    <linearGradient id="colorWithInterest" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--income)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="var(--income)" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorWithoutInterest" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--chart-2)" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="var(--chart-2)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" opacity={0.3} vertical={false} />
                  <XAxis
                    dataKey="month"
                    tick={{
                      fill: "hsl(var(--muted-foreground))",
                      fontSize: 11,
                    }}
                    tickLine={false}
                    axisLine={{ stroke: "hsl(var(--border))", strokeWidth: 1 }}
                    interval={xAxisInterval}
                  />
                  <YAxis
                    tick={{
                      fill: "hsl(var(--muted-foreground))",
                      fontSize: 11,
                    }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={formatCurrency}
                    width={60}
                  />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        formatter={(value) => formatCurrencyFull(value as number)}
                      />
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="withoutInterest"
                    stroke="var(--chart-2)"
                    fillOpacity={1}
                    fill="url(#colorWithoutInterest)"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    {...({ isAnimationActive: false } as any)}
                  />
                  <Area
                    type="monotone"
                    dataKey="withInterest"
                    stroke="var(--income)"
                    fillOpacity={1}
                    fill="url(#colorWithInterest)"
                    strokeWidth={2.5}
                    {...({ isAnimationActive: false } as any)}
                  />
                </AreaChart>
              </ChartContainer>
            </div>
          )}

          <div className="flex items-center justify-center gap-6 pt-2">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-income" />
              <span className="text-xs sm:text-sm text-muted-foreground">
                With Interest ({interestRate[0].toFixed(1)}%)
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-chart-2" />
              <span className="text-xs sm:text-sm text-muted-foreground">Without Interest</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Interactive Controls */}
      <Card className="p-6">
        <h3 className="text-base font-semibold">Adjust Parameters</h3>

        <div className="space-y-8">
          {/* Income Slider */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-income" />
                <label className="text-sm font-medium">Monthly Income</label>
              </div>
              <Input
                type="number"
                value={currentIncome}
                onChange={handleIncomeChange}
                onKeyDown={handleKeyDown}
                min={0}
                max={20000}
                step={100}
                className="w-32 text-right font-bold text-income"
              />
            </div>
            <Slider
              value={income}
              onValueChange={setIncome}
              min={0}
              max={20000}
              step={100}
              className="[&_[data-slot=slider-range]]:bg-income"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>$0</span>
              <span>$20,000</span>
            </div>
          </div>

          {/* Expenses Slider */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-expense" />
                <label className="text-sm font-medium">Monthly Expenses</label>
              </div>
              <Input
                type="number"
                value={currentExpenses}
                onChange={handleExpensesChange}
                onKeyDown={handleKeyDown}
                min={0}
                max={20000}
                step={100}
                className="w-32 text-right font-bold text-expense"
              />
            </div>
            <Slider
              value={expenses}
              onValueChange={setExpenses}
              min={0}
              max={20000}
              step={100}
              className="[&_[data-slot=slider-range]]:bg-expense"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>$0</span>
              <span>$20,000</span>
            </div>
          </div>

          {/* Timeline Slider */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-chart-2" />
                <label className="text-sm font-medium">Timeline</label>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={timelineYears[0]}
                  onChange={handleTimelineChange}
                  onKeyDown={handleKeyDown}
                  min={1}
                  max={60}
                  step={1}
                  className="w-20 text-right font-bold text-chart-2"
                />
                <span className="text-sm font-medium text-chart-2">
                  {timelineYears[0] === 1 ? 'year' : 'years'}
                </span>
              </div>
            </div>
            <Slider
              value={timelineYears}
              onValueChange={setTimelineYears}
              min={1}
              max={60}
              step={1}
              className="[&_[data-slot=slider-range]]:bg-chart-2"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1 year</span>
              <span>60 years</span>
            </div>
          </div>

          {/* Interest Rate Slider */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                <label className="text-sm font-medium">Annual Interest Rate</label>
              </div>
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  value={interestRateInput}
                  onChange={handleInterestRateChange}
                  onBlur={handleInterestRateBlur}
                  onKeyDown={handleKeyDown}
                  min={0}
                  max={10}
                  step={0.1}
                  className="w-20 text-right font-bold text-primary"
                />
                <span className="text-sm font-bold text-primary">%</span>
              </div>
            </div>
            <Slider
              value={interestRate}
              onValueChange={handleInterestRateSliderChange}
              min={0}
              max={10}
              step={0.1}
              className="[&_[data-slot=slider-range]]:bg-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0%</span>
              <span>10%</span>
            </div>
          </div>
        </div>
      </Card>
      </div>
    </div>
  )
}
